
import { useState, useEffect } from 'react';

export default function Home() {
  const [activeSection, setActiveSection] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const sections = [
    {
      title: "Software Solutions",
      subtitle: "Comprehensive Technology Solutions for Modern Business",
      description: "We deliver cutting-edge software solutions that transform businesses and drive digital innovation. Our expertise spans across multiple domains to meet your unique technological needs.",
      bgImage: "https://readdy.ai/api/search-image?query=Modern%20software%20development%20office%20with%20multiple%20screens%20displaying%20code%20and%20digital%20interfaces%2C%20high-tech%20workspace%20with%20ambient%20lighting%2C%20clean%20minimalist%20design%2C%20professional%20atmosphere%2C%20ultra-high%20resolution%204K%20quality&width=1920&height=1080&seq=software-solutions-bg&orientation=landscape",
      icon: "ri-code-s-slash-line"
    },
    {
      title: "Custom Software Development",
      subtitle: "Tailored Solutions for Your Business",
      description: "Build bespoke software applications designed specifically for your business processes. From concept to deployment, we create scalable and maintainable solutions that grow with your organization.",
      bgImage: "https://readdy.ai/api/search-image?query=Software%20developers%20working%20on%20custom%20applications%20with%20multiple%20monitors%20showing%20code%20editors%20and%20design%20interfaces%2C%20modern%20development%20environment%20with%20clean%20workspace%2C%20professional%20coding%20atmosphere%2C%204K%20ultra-high%20definition&width=1920&height=1080&seq=custom-dev-bg&orientation=landscape",
      icon: "ri-settings-3-line",
      features: ["Custom Architecture Design", "Scalable Solutions", "API Integration", "Quality Assurance"]
    },
    {
      title: "Web Development",
      subtitle: "Websites & Web Applications",
      description: "Create stunning websites and powerful web applications that engage users and drive results. From responsive design to complex web platforms, we deliver exceptional digital experiences.",
      bgImage: "https://readdy.ai/api/search-image?query=Modern%20web%20development%20workspace%20with%20designer%20creating%20responsive%20websites%20on%20large%20monitors%2C%20clean%20interface%20designs%20visible%20on%20screens%2C%20contemporary%20office%20environment%20with%20natural%20lighting%2C%20premium%20quality%204K%20resolution&width=1920&height=1080&seq=web-dev-bg&orientation=landscape",
      icon: "ri-global-line",
      features: ["Responsive Design", "Progressive Web Apps", "E-commerce Integration", "Performance Optimization"]
    },
    {
      title: "Mobile App Development",
      subtitle: "iOS & Android Applications",
      description: "Develop native and cross-platform mobile applications that deliver seamless user experiences. From concept to app store deployment, we handle the entire mobile development lifecycle.",
      bgImage: "https://readdy.ai/api/search-image?query=Mobile%20app%20development%20studio%20with%20developers%20testing%20applications%20on%20various%20devices%20and%20tablets%2C%20modern%20workspace%20with%20multiple%20mobile%20screens%20displaying%20app%20interfaces%2C%20high-tech%20environment%2C%20crystal%20clear%204K%20quality&width=1920&height=1080&seq=mobile-dev-bg&orientation=landscape",
      icon: "ri-smartphone-line",
      features: ["Native iOS Development", "Android Applications", "Cross-Platform Solutions", "App Store Optimization"]
    },
    {
      title: "Enterprise Resource Planning (ERP)",
      subtitle: "Streamline Business Operations",
      description: "Implement comprehensive ERP systems that integrate all business processes into a unified platform. Improve efficiency, reduce costs, and gain real-time insights into your operations.",
      bgImage: "https://readdy.ai/api/search-image?query=Enterprise%20business%20operations%20center%20with%20large%20dashboards%20displaying%20ERP%20system%20analytics%20and%20data%20visualizations%2C%20professional%20corporate%20environment%20with%20modern%20technology%20displays%2C%20sophisticated%20business%20intelligence%20setup%2C%204K%20ultra-high%20resolution&width=1920&height=1080&seq=erp-bg&orientation=landscape",
      icon: "ri-building-line",
      features: ["Process Automation", "Real-time Analytics", "Multi-department Integration", "Scalable Architecture"]
    },
    {
      title: "Customer Relationship Management (CRM)",
      subtitle: "Enhance Customer Relationships",
      description: "Deploy advanced CRM solutions that help you manage customer interactions, track sales, and improve customer satisfaction. Build stronger relationships and drive revenue growth.",
      bgImage: "https://readdy.ai/api/search-image?query=Customer%20service%20center%20with%20representatives%20using%20CRM%20software%20on%20modern%20workstations%2C%20professional%20call%20center%20environment%20with%20large%20screens%20showing%20customer%20data%20and%20analytics%2C%20bright%20contemporary%20office%20space%2C%20premium%204K%20quality&width=1920&height=1080&seq=crm-bg&orientation=landscape",
      icon: "ri-user-heart-line",
      features: ["Lead Management", "Sales Pipeline", "Customer Analytics", "Automated Workflows"]
    },
    {
      title: "E-commerce Platforms",
      subtitle: "Complete Online Store Solutions",
      description: "Build powerful e-commerce platforms that drive sales and provide exceptional shopping experiences. From catalog management to payment processing, we cover all aspects of online retail.",
      bgImage: "https://readdy.ai/api/search-image?query=Modern%20e-commerce%20fulfillment%20center%20with%20automated%20systems%20and%20digital%20displays%20showing%20online%20store%20metrics%2C%20high-tech%20warehouse%20environment%20with%20sophisticated%20inventory%20management%20systems%2C%20professional%20commercial%20space%2C%204K%20ultra-definition&width=1920&height=1080&seq=ecommerce-bg&orientation=landscape",
      icon: "ri-shopping-cart-line",
      features: ["Multi-channel Sales", "Inventory Management", "Payment Integration", "Analytics Dashboard"]
    },
    {
      title: "SaaS Products",
      subtitle: "Software as a Service Solutions",
      description: "Develop scalable SaaS products that serve thousands of users globally. From architecture design to continuous deployment, we build cloud-native applications that scale effortlessly.",
      bgImage: "https://readdy.ai/api/search-image?query=Cloud%20computing%20data%20center%20with%20servers%20and%20digital%20infrastructure%20supporting%20SaaS%20applications%2C%20modern%20technology%20facility%20with%20ambient%20lighting%20and%20sophisticated%20monitoring%20systems%2C%20high-tech%20server%20environment%2C%20crystal%20clear%204K%20resolution&width=1920&height=1080&seq=saas-bg&orientation=landscape",
      icon: "ri-cloud-line",
      features: ["Multi-tenant Architecture", "Auto-scaling Infrastructure", "API-first Design", "Global CDN Distribution"]
    }
  ];

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Dynamic Background */}
      <div className="fixed inset-0 -z-10">
        {sections.map((section, index) => (
          <div
            key={index}
            className={`absolute inset-0 bg-cover bg-center bg-fixed transition-opacity duration-1000 ${
              activeSection === index ? 'opacity-100' : 'opacity-0'
            }`}
            style={{
              backgroundImage: `url(${section.bgImage})`,
              transform: `translate(${mousePosition.x * -0.01}px, ${mousePosition.y * -0.01}px)`,
            }}
          >
            <div className="absolute inset-0 bg-black/40"></div>
          </div>
        ))}
      </div>

      {/* 3D Floating Elements */}
      <div className="fixed inset-0 pointer-events-none -z-5">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="absolute animate-pulse"
            style={{
              left: `${20 + i * 15}%`,
              top: `${10 + i * 12}%`,
              transform: `translateZ(${i * 10}px) rotateX(${i * 15}deg) rotateY(${i * 20}deg)`,
              animationDelay: `${i * 0.5}s`,
              animationDuration: `${3 + i}s`
            }}
          >
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-xl backdrop-blur-sm border border-white/10 shadow-2xl transform rotate-45 hover:scale-110 transition-transform duration-500"></div>
          </div>
        ))}
      </div>

      {/* Main Content */}
      <div className="relative z-10">
        {sections.map((section, index) => (
          <section
            key={index}
            className="min-h-screen flex items-center justify-center px-8 py-16 relative group"
            onMouseEnter={() => setActiveSection(index)}
          >
            <div className="max-w-7xl mx-auto w-full">
              <div className="grid lg:grid-cols-2 gap-16 items-center">
                {/* Content */}
                <div className={`transform transition-all duration-1000 ${
                  activeSection === index 
                    ? 'translate-x-0 opacity-100' 
                    : 'translate-x-[-50px] opacity-70'
                }`}>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl shadow-2xl transform hover:scale-110 transition-all duration-300 hover:rotate-3">
                      <i className={`${section.icon} text-2xl text-white`}></i>
                    </div>
                    <div className="h-px bg-gradient-to-r from-blue-500 to-transparent flex-1"></div>
                  </div>
                  
                  <h1 className="text-6xl font-bold text-white mb-4 tracking-tight">
                    {section.title}
                  </h1>
                  
                  <h2 className="text-2xl text-blue-200 mb-8 font-light">
                    {section.subtitle}
                  </h2>
                  
                  <p className="text-lg text-gray-200 mb-8 leading-relaxed">
                    {section.description}
                  </p>
                  
                  {section.features && (
                    <div className="grid grid-cols-2 gap-4 mb-8">
                      {section.features.map((feature, featureIndex) => (
                        <div
                          key={featureIndex}
                          className="flex items-center gap-3 p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:scale-105 cursor-pointer"
                        >
                          <i className="ri-check-line text-green-400 text-xl"></i>
                          <span className="text-white font-medium">{feature}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <button className="group inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold text-lg shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-105 hover:rotate-1 whitespace-nowrap cursor-pointer">
                    <span>Learn More</span>
                    <i className="ri-arrow-right-line text-xl group-hover:translate-x-2 transition-transform duration-300"></i>
                  </button>
                </div>

                {/* Interactive 3D Card */}
                <div className={`transform transition-all duration-1000 delay-200 ${
                  activeSection === index 
                    ? 'translate-x-0 opacity-100 scale-100' 
                    : 'translate-x-[50px] opacity-70 scale-95'
                }`}>
                  <div className="relative group cursor-pointer">
                    <div className="absolute -inset-4 bg-gradient-to-r from-blue-500/50 to-purple-600/50 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
                    
                    <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20 shadow-2xl group-hover:bg-white/20 transition-all duration-500 transform group-hover:scale-105 group-hover:rotate-1">
                      <div className="space-y-6">
                        <div className="flex items-center justify-between">
                          <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl">
                            <i className={`${section.icon} text-xl text-white`}></i>
                          </div>
                          <div className="flex gap-2">
                            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                            <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                          </div>
                        </div>
                        
                        <div className="space-y-4">
                          <div className="h-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full"></div>
                          <div className="space-y-2">
                            <div className="h-1 bg-white/30 rounded-full w-3/4"></div>
                            <div className="h-1 bg-white/30 rounded-full w-1/2"></div>
                            <div className="h-1 bg-white/30 rounded-full w-2/3"></div>
                          </div>
                        </div>
                        
                        <div className="flex justify-between items-center pt-4">
                          <div className="flex gap-2">
                            {[...Array(4)].map((_, i) => (
                              <div key={i} className="w-8 h-8 bg-gradient-to-br from-blue-400 to-purple-500 rounded-lg opacity-80"></div>
                            ))}
                          </div>
                          <div className="text-white/60 text-sm font-mono">
                            {String(index + 1).padStart(2, '0')}/08
                          </div>
                        </div>
                      </div>
                      
                      {/* 3D Hover Effect Elements */}
                      <div className="absolute top-4 right-4 w-16 h-16 bg-gradient-to-br from-blue-500/30 to-purple-600/30 rounded-xl transform rotate-12 group-hover:rotate-45 transition-transform duration-500"></div>
                      <div className="absolute bottom-4 left-4 w-12 h-12 bg-gradient-to-br from-purple-500/30 to-pink-600/30 rounded-lg transform -rotate-12 group-hover:-rotate-45 transition-transform duration-500"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Section Indicator */}
            <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
              <div className="flex gap-2">
                {sections.map((_, i) => (
                  <button
                    key={i}
                    className={`w-3 h-3 rounded-full transition-all duration-300 cursor-pointer ${
                      activeSection === i 
                        ? 'bg-white scale-125' 
                        : 'bg-white/30 hover:bg-white/50'
                    }`}
                    onClick={() => setActiveSection(i)}
                  ></button>
                ))}
              </div>
            </div>
            
            {/* Scroll Indicator */}
            {index < sections.length - 1 && (
              <div className="absolute bottom-4 right-8">
                <div className="animate-bounce">
                  <i className="ri-arrow-down-line text-2xl text-white/60"></i>
                </div>
              </div>
            )}
          </section>
        ))}
      </div>
      
      {/* Floating Navigation */}
      <div className="fixed right-8 top-1/2 transform -translate-y-1/2 z-20">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 border border-white/20 shadow-2xl">
          <nav className="space-y-4">
            {sections.map((section, index) => (
              <button
                key={index}
                onClick={() => setActiveSection(index)}
                className={`block w-12 h-12 rounded-xl transition-all duration-300 flex items-center justify-center cursor-pointer ${
                  activeSection === index 
                    ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white scale-110' 
                    : 'bg-white/10 text-white/60 hover:bg-white/20 hover:text-white'
                }`}
                title={section.title}
              >
                <i className={`${section.icon} text-lg`}></i>
              </button>
            ))}
          </nav>
        </div>
      </div>
    </div>
  );
}
